import { memo, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGlobalSettings } from 'providers';
import { useLocationParams } from 'hooks';
import { idsFromSelection } from 'utils';
import type { PinterestFormValues } from '../validation/pinterest.schema';
import { pinterestSchema } from '../validation/pinterest.schema';
import { getPinterestFormDefaultValues } from '../validation/defaultValues';
import PinterestForm from './PinterestForm';

const PinterestFormProvider = memo(() => {
  const { selectedApp } = useGlobalSettings();
  const {
    app: appParam,
    adAccount: adAccountParam,
    flatCreatives: flatCreativesParam,
    creativesFormat: creativesFormatParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    filterCreativesByName: filterCreativesByNameParam,
    filterCampaignsByName: filterCampaignsByNameParam,
    filterAdSetsByName: filterAdSetsByNameParam,
  } = useLocationParams();

  const defaultValues = useMemo<PinterestFormValues>(
    () => ({
      ...getPinterestFormDefaultValues(appParam ?? selectedApp ?? ''),
      accountId: adAccountParam ?? '',
      selectedCreatives: creativesFormatParam === 'flat' ? flatCreativesParam : [],
      filterCreativeByName: filterCreativesByNameParam,
      selectedCampaigns: idsFromSelection(selectedCampaignsParam),
      filterCampaignsByName: filterCampaignsByNameParam,
      selectedAdGroups: idsFromSelection(selectedAdSetsParam),
      filterAdGroupsByName: filterAdSetsByNameParam,
    }),
    [
      adAccountParam,
      appParam,
      creativesFormatParam,
      filterAdSetsByNameParam,
      filterCampaignsByNameParam,
      filterCreativesByNameParam,
      flatCreativesParam,
      selectedAdSetsParam,
      selectedApp,
      selectedCampaignsParam,
    ],
  );

  const formMethods = useForm<PinterestFormValues>({
    resolver: yupResolver(pinterestSchema),
    defaultValues,
    mode: 'onChange',
  });

  return (
    <FormProvider {...formMethods}>
      <PinterestForm />
    </FormProvider>
  );
});

PinterestFormProvider.displayName = 'PinterestFormProvider';

export default PinterestFormProvider;
